import { MarkdownRenderer } from "@/components/MarkdownRenderer";
import {
  BodyText,
  Button,
  Callout,
  HeaderText,
  Separator,
  Skeleton,
} from "@/components/ui";
import { updateSubjectiveSummaryTag } from "@/features/Charting/components/Subjective/subjectiveText";
import { useGetPatient } from "@/features/Patient/network/usePatient";
import { isUniversalClinicPatient } from "@/features/Patient/utils";
import { hasMarkdownFormatting } from "@/lib/utils";
import { CarePlan, Encounter } from "@curai/js-fhir-tools";
import { Link, useParams } from "@tanstack/react-router";
import { format } from "date-fns";
import {
  Check,
  CircleSlash,
  ClipboardPlus,
  Info,
  Loader,
  OctagonX,
} from "lucide-react";
import { useMemo } from "react";
import { FilePreview } from "./FilePreview";
import { Lab } from "./Lab";
import { Medication } from "./Medication";
import { Signatures } from "./Signatures";
import { useTimelineEncounter } from "./network/useTimelineEncounter";
import { EncountersResponse_EncounterStatus } from "./network/useTimelineEncounters";

interface EncounterCardDetailsProps {
  encounterId: string;
  motherEncounterId?: string;
  encounterStatus: EncountersResponse_EncounterStatus;
  classFhir: string;
}

export const EncounterCardDetails = ({
  encounterId,
  motherEncounterId,
  encounterStatus,
  classFhir,
}: EncounterCardDetailsProps) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });

  const { data, isLoading, isError } = useTimelineEncounter({
    patientId,
    encounterId,
    motherEncounterId,
  });

  const { data: patient } = useGetPatient(patientId as string);

  const patientIsUniversalClinic = isUniversalClinicPatient(patient);

  const encounterModel = useMemo(() => {
    if (!data?.encounter) return;
    return new Encounter(data?.encounter);
  }, [data?.encounter]);

  const careplan = useMemo(() => {
    if (!data?.careplan) return;
    return new CarePlan(data?.careplan);
  }, [data?.careplan]);

  const status = useMemo(() => {
    switch (encounterStatus) {
      case EncountersResponse_EncounterStatus.ARRIVED:
        return {
          Icon: Loader,
          label: "Arrived",
        };
      case EncountersResponse_EncounterStatus.IN_PROGRESS:
        return {
          Icon: Loader,
          label: "In Progress",
        };
      case EncountersResponse_EncounterStatus.COMPLETED:
        return {
          Icon: Check,
          label: "Completed",
        };
      case EncountersResponse_EncounterStatus.INCOMPLETE:
        return {
          Icon: CircleSlash,
          label: "Incomplete",
        };
      case EncountersResponse_EncounterStatus.CANCELLED:
        return {
          Icon: OctagonX,
          label: "Cancelled",
        };
      case EncountersResponse_EncounterStatus.IGNORED:
        return {
          Icon: Info,
          label: "Ignored",
        };
      default:
        return {
          Icon: Info,
          label: "Unknown",
        };
    }
  }, [encounterStatus]);

  const isAsync = classFhir === "ASYNC";

  if (!data || isLoading)
    return (
      <div className="flex flex-col gap-1">
        <Skeleton className="w-1/2 h-8" />
        <Skeleton className="w-4/5 h-4" />
        <Skeleton className="w-3/4 h-4" />
        <Skeleton className="w-4/5 h-4" />
        <Skeleton className="w-4/5 h-4" />
      </div>
    );

  if (isError) {
    return (
      <Callout
        title="Something went wrong"
        variant="danger"
        description="Unable to load encounter."
      />
    );
  }

  return (
    <div className="flex flex-col">
      {!!data?.adminNotes.length && (
        <section
          className="flex flex-col gap-2"
          data-testid="encounter-admin-notes"
        >
          <BodyText size="lg" bold>
            Admin Notes
          </BodyText>
          {data.adminNotes.map((adminNote) =>
            adminNote.note?.map(({ text }) => {
              // For Universal Clinic patients, show sender instead of recipient
              const creator = patientIsUniversalClinic
                ? adminNote.sender?.display
                : adminNote.recipient?.[0]?.display;

              return (
                <div className="flex flex-col gap-1" key={adminNote.id}>
                  <BodyText tag="p" size="sm">
                    {text}
                  </BodyText>
                  {creator && adminNote.received && (
                    <div className="flex items-center gap-1">
                      <ClipboardPlus size={12} />
                      <BodyText tag="p" size="xs" textColor="secondary">
                        Created by: {creator} on{" "}
                        {format(new Date(adminNote.received), "MM/dd/yyyy")} at{" "}
                        {format(new Date(adminNote.received), "h:mma")}
                      </BodyText>
                    </div>
                  )}
                </div>
              );
            }),
          )}
        </section>
      )}

      <section
        className="flex flex-col gap-3"
        data-testid="encounter-assessment"
      >
        {!!data?.adminNotes.length && <Separator className="my-3" />}
        <HeaderText tag="h3" size="lg">
          Assessment
        </HeaderText>

        <BodyText tag="p" size="sm" preserveLineBreaks>
          {data?.assessment?.description || "N/A"}
        </BodyText>
      </section>

      {careplan && (
        <section
          className="flex flex-col gap-3"
          data-testid="encounter-careplan"
        >
          <Separator className="my-3" />

          <HeaderText tag="h3" size="lg">
            Care Plan
          </HeaderText>

          {!!data?.medicalRequests?.length && (
            <>
              <BodyText size="md" bold>
                Medications/OTC
              </BodyText>

              {data?.medicalRequests.map((medication) => (
                <Medication key={medication.id} medication={medication} />
              ))}
            </>
          )}

          {!!data?.serviceRequests?.length && (
            <>
              <BodyText size="md" bold>
                Workup
              </BodyText>

              {data?.serviceRequests.map((serviceRequest) => (
                <Lab key={serviceRequest.id} labTest={serviceRequest} />
              ))}
            </>
          )}

          {!!careplan.supportiveCare?.length && (
            <>
              <HeaderText tag="h4" size="md">
                Supportive Care
              </HeaderText>

              <ul>
                {careplan.supportiveCare.map((description) => (
                  <div key={description}>
                    {hasMarkdownFormatting(description) ? (
                      <MarkdownRenderer content={description} />
                    ) : (
                      <BodyText size="sm" preserveLineBreaks>
                        {description}
                      </BodyText>
                    )}
                  </div>
                ))}
              </ul>
            </>
          )}

          {!!careplan.disposition?.length && (
            <>
              <HeaderText tag="h4" size="md">
                Disposition
              </HeaderText>

              {careplan.disposition.map(
                (description) =>
                  description && (
                    <BodyText
                      tag="p"
                      size="sm"
                      key={description}
                      preserveLineBreaks
                    >
                      {description}
                    </BodyText>
                  ),
              )}
            </>
          )}

          {!!careplan.referOut?.length && (
            <>
              <HeaderText tag="h4" size="md">
                Refer Out
              </HeaderText>

              {careplan.referOut.map((description) => (
                <BodyText
                  tag="p"
                  size="sm"
                  key={description}
                  preserveLineBreaks
                >
                  {description}
                </BodyText>
              ))}
            </>
          )}

          <HeaderText tag="h4" size="md">
            Follow-Up
          </HeaderText>
          <div className="flex flex-col gap-1">
            <BodyText tag="p" size="sm" bold>
              Timing
            </BodyText>
            <BodyText tag="p" size="sm">
              {careplan.followupDuration || "N/A"} {careplan.followupUnit}
            </BodyText>
          </div>
          <div className="flex flex-col gap-1">
            <BodyText tag="p" size="sm" bold>
              Message
            </BodyText>
            <BodyText tag="p" size="sm" preserveLineBreaks>
              {careplan.followupMessage || "N/A"}
            </BodyText>
          </div>
        </section>
      )}

      {data?.subjective?.description && (
        <section
          className="flex flex-col gap-3"
          data-testid="encounter-subjective"
        >
          <Separator className="my-3" />

          <HeaderText tag="h3" size="lg">
            Subjective
          </HeaderText>
          {(() => {
            const subjectiveText = updateSubjectiveSummaryTag(data.subjective);
            return hasMarkdownFormatting(subjectiveText) ? (
              <MarkdownRenderer content={subjectiveText} />
            ) : (
              <BodyText tag="p" size="sm" preserveLineBreaks>
                {subjectiveText}
              </BodyText>
            );
          })()}
        </section>
      )}

      {!!data?.objectives?.length && (
        <section
          className="flex flex-col gap-3"
          data-testid="encounter-objective"
        >
          <Separator className="my-3" />

          <HeaderText tag="h3" size="lg">
            Objective
          </HeaderText>

          {data?.objectives.map(({ code, valueString }) => (
            <div className="" key={valueString}>
              <BodyText tag="p" size="sm" bold>
                {code.text || code.coding?.[0]?.display}
              </BodyText>
              <BodyText tag="p" size="sm">
                {valueString}
              </BodyText>
            </div>
          ))}
        </section>
      )}

      <Link
        to={"/patient/$patientId/timeline"}
        params={{ patientId }}
        search={{
          encounterId: motherEncounterId,
          chatTab: isAsync ? ("async" as const) : undefined,
        }}
      >
        <Button className="mt-2 w-fit" size="sm">
          {encounterModel?.amazonId ? "See CIQ" : "See Visit"}
        </Button>
      </Link>

      {!!data?.media.length && (
        <section className="flex flex-col gap-3" data-testid="encounter-files">
          <Separator className="my-3" />

          <BodyText size="lg" bold>
            Files
          </BodyText>

          {data?.media.map((media) => (
            <div className="flex flex-col gap-2" key={media.id}>
              <FilePreview media={media} />
            </div>
          ))}
        </section>
      )}

      {!!data?.billingCodes && (
        <section
          className="flex flex-col gap-3"
          data-testid="encounter-billing"
        >
          <Separator className="my-3" />

          <BodyText size="lg" bold>
            ICD-10 Codes
          </BodyText>

          <div className="mt-1 flex flex-col gap-2">
            {data.billingCodes.length ? (
              data.billingCodes.map(({ code, display }) => (
                <BodyText size="sm" key={code + display}>
                  {code} - {display}
                </BodyText>
              ))
            ) : (
              <BodyText size="sm" textColor="tertiary">
                No ICD-10 Codes
              </BodyText>
            )}
          </div>
        </section>
      )}

      <section className="mt-4">
        <span className="flex items-center gap-1">
          <status.Icon size="12px" />
          <BodyText tag="span" size="xs" textColor="secondary">
            {status.label}
          </BodyText>
        </span>

        <Signatures encounter={data.encounter} />
        {encounterModel?.amazonId && (
          <BodyText size="xs" textColor="tertiary">
            AMZ Visit ID: {encounterModel.amazonId}
          </BodyText>
        )}
      </section>
    </div>
  );
};
